import Icon, { type IconName } from '../ui/Icon';
import { useState, useRef, useEffect } from 'react';
import { useUser, useClerk } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { smsApi, documentsApi, incidentsApi, notificationsApi } from '../../lib/api';
import GlobalSearch from './GlobalSearch';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useTenantSwitch } from '../../contexts/TenantSwitchContext';
import { useBranding } from '../../contexts/BrandingContext';
import { IS_DEMO_BUILD } from '../../lib/demoMode';
import ThemeToggle from '../ui/ThemeToggle';
import { useConfirm } from '../ConfirmHost';

interface TopBarProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export default function TopBar({ onMenuClick, showMenuButton }: TopBarProps = {}) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  // Close on an outside click or Escape — a menu that traps you is worse
  // than no menu.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!userMenuRef.current?.contains(e.target as Node)) setUserMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  // Confirm before ending the session. signOut clears this device's
  // Microsoft (or demo) session, so it is not something to trigger by a stray click.
  async function handleSignOut() {
    setUserMenuOpen(false);
    const ok = await confirm({
      title: 'Sign out?',
      description: 'You will need to sign in again to get back in. This signs you out on this device.',
      confirmLabel: 'Sign out',
      cancelLabel: 'Stay signed in',
      destructive: true,
    });
    if (ok) void signOut();
  }

  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const { branding } = useBranding();
  // Platform-operator tenant switcher (platform/tenant control-plane split, Wave 2).
  // Only holders of platform.tenants.impersonate (founders via failsafe) ever see
  // these controls; a normal FNS user sees nothing new.
  const canImpersonate = has('platform.tenants.impersonate');
  const { activeTenantId, activeTenantName, returnToPlatform } = useTenantSwitch();
  // Integration status is admin-internal — the old vendor chip bar is
  // replaced by the notification bell; admins reach the status page at
  // /settings/integrations (linked from the bell's footer).
  const canSeeIntegrations = has('admin.integrations.manage') && !IS_DEMO_BUILD;
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!bellOpen) return;
    const onDown = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [bellOpen]);

  const initials = user
    ? `${(user.firstName ?? 'U')[0]}${(user.lastName ?? '')[0] ?? ''}`.toUpperCase()
    : 'U';

  const fullName = user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? 'User';

  // Alert counts
  const { data: smsData } = useQuery({
    queryKey: ['sms-pending-count'],
    queryFn: () => smsApi.list({ status: 'pending' }),
    refetchInterval: 30000,
  });

  const { data: qaData } = useQuery({
    queryKey: ['qa-pending-count'],
    queryFn: () => documentsApi.pendingQA(),
    refetchInterval: 30000,
  });

  const { data: incidentsData } = useQuery({
    queryKey: ['incidents-open-count'],
    queryFn: () => incidentsApi.list({ status: 'open' }),
    refetchInterval: 60000,
  });

  // Notification feed for the bell.
  const { data: feedData } = useQuery({
    queryKey: ['notification-feed'],
    queryFn: () => notificationsApi.feed(),
    refetchInterval: 60_000,
  });
  const feed = feedData?.data?.notifications ?? [];
  const unread = feedData?.data?.unread ?? 0;

  const openNotification = async (n: { id: string; link: string | null; read_at: string | null }) => {
    if (!n.read_at) {
      try { await notificationsApi.markRead(n.id); } catch { /* best-effort */ }
      void queryClient.invalidateQueries({ queryKey: ['notification-feed'] });
    }
    setBellOpen(false);
    if (n.link) navigate(n.link);
  };

  const smsPending = smsData?.data?.approvals?.length ?? 0;
  const qaPending = (qaData?.data as { questions?: unknown[] })?.questions?.length ?? 0;
  const incidentsOpen = incidentsData?.data?.incidents?.length ?? 0;

  return (
    <>
      {/* Impersonation banner — unmissable colored bar shown app-wide whenever a
          platform operator is scoped into a tenant. Sits in the app-shell grid's
          row 1 (same slot the View-as-Role banner uses) so it pushes content down
          rather than overlaying it. Persistent until the operator explicitly
          returns to the platform. */}
      {activeTenantId && (
        <div
          role="status"
          style={{ gridColumn: '1 / -1', gridRow: 1, background: 'linear-gradient(90deg, var(--ai), var(--ai))', color: '#fff', padding: '10px 20px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, boxShadow: '0 2px 6px rgba(0,0,0,0.15)', zIndex: 120 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <Icon name="building" size={16} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Viewing as <strong>{activeTenantName ?? 'tenant'}</strong> — you are scoped into this tenant's data.
            </span>
          </div>
          <button
            type="button"
            onClick={() => returnToPlatform()}
            style={{ padding: '6px 14px', background: 'var(--surface)', color: 'var(--ai)', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Return to platform
          </button>
        </div>
      )}
      <header
        className="topbar"
        style={showMenuButton ? {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        } : undefined}
      >
      {/* Hamburger menu button — mobile only */}
      {showMenuButton && (
        <button
          onClick={onMenuClick}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px', marginRight: 8, color: 'inherit', fontSize: 20, lineHeight: 1, display: 'flex', alignItems: 'center' }}
          aria-label="Toggle menu"
        >
          <Icon name="menu" size={18} />
        </button>
      )}

      {/* Logo — per-tenant branding (white-label Tranche 3). */}
      <div className="topbar-logo" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => navigate('/dashboard')}>
        {branding.logo_url && (
          <img src={branding.logo_url} alt="" style={{ height: 26, width: 'auto', borderRadius: 4 }} />
        )}
        <span>
          {branding.app_name}
          {branding.tagline && <small>{branding.tagline}</small>}
        </span>
      </div>

      {/* Global search */}
      <GlobalSearch />

      {/* Notification bell — replaces the old vendor chip bar. Admins
          reach integration status via the dropdown footer link. */}
      {/* `marginLeft: auto` is what separates the left group (logo + search)
          from the right group (bell → theme → help → account). The old
          `.topbar-integrations` element carried `flex: 1` and did this job;
          when the vendor chip bar was replaced by this bell, nothing took
          the slack over, so every control bunched up against the search box
          and left dead space at the right edge. This is the spacer now. */}
      <div ref={bellRef} className="ml-auto" style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setBellOpen((v) => !v)}
          title="Notifications"
          aria-label="Notifications"
          style={{ position: 'relative', background: 'transparent', border: '1px solid var(--bd, var(--border))', borderRadius: 8, padding: '6px 10px', fontSize: 15, cursor: 'pointer', lineHeight: 1 }}
        >
          <Icon name="bell" size={17} />
          {unread > 0 && (
            <span style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', borderRadius: 999, fontSize: 10, fontWeight: 700, padding: '1px 5px', minWidth: 16, textAlign: 'center' }}>{unread > 99 ? '99+' : unread}</span>
          )}
        </button>
        {bellOpen && (
          <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 360, maxHeight: 440, overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 12px 32px rgba(15, 23, 42, .14)', zIndex: 300 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--surface-subtle)' }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-strong)' }}>Notifications</span>
              {unread > 0 && (
                <button type="button"
                  onClick={async () => { try { await notificationsApi.markAllRead(); } catch { /* best-effort */ } void queryClient.invalidateQueries({ queryKey: ['notification-feed'] }); }}
                  style={{ background: 'none', border: 'none', color: 'var(--info)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Mark all read
                </button>
              )}
            </div>
            {feed.length === 0 ? (
              <div style={{ padding: 20, fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>You're all caught up.</div>
            ) : feed.slice(0, 15).map((n) => (
              <div key={n.id} onClick={() => void openNotification(n)}
                style={{ padding: '10px 14px', borderBottom: '1px solid var(--surface-sunken)', cursor: 'pointer', background: n.read_at ? '#fff' : 'var(--info-tint)' }}>
                <div style={{ fontSize: 13, fontWeight: n.read_at ? 500 : 700, color: 'var(--text-strong)' }}>{n.title}</div>
                {n.body && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{n.body.slice(0, 120)}</div>}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px' }}>
              <button type="button" onClick={() => { setBellOpen(false); navigate('/notifications'); }}
                style={{ background: 'none', border: 'none', color: 'var(--info)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                All notifications →
              </button>
              {canSeeIntegrations && (
                <button type="button" onClick={() => { setBellOpen(false); navigate('/settings/integrations'); }}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="settings" size={13} /> Integration status</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Light / dark theme toggle — persists to localStorage (lib/theme). */}
      <div style={{ marginLeft: 8, color: 'var(--sf)', display: 'flex', alignItems: 'center' }}>
        <ThemeToggle />
      </div>

      {/* Always-visible Help button — pinned to the top bar so it's
          reachable from any page regardless of sidebar state. Subtle
          ghost styling so it doesn't fight the alert badges next to it. */}
      <button
        type="button"
        onClick={() => navigate('/help')}
        title="Open the Help Center"
        aria-label="Help"
        // Tailwind (new styling goes here rather than inline styles).
        // Colors are white-alpha, not --t2/--bd: the top bar is a dark band in
        // BOTH themes (brand blue in light, --sf in dark), so the body text
        // token this used to carry rendered near-black on dark blue and the
        // button was effectively invisible in light mode. White-alpha also
        // matches the theme toggle sitting next to it.
        className="ml-2 flex cursor-pointer items-center gap-1.5 whitespace-nowrap
                   rounded-lg border border-white/25 bg-transparent px-3 py-1.5
                   text-xs font-semibold text-white/90 transition-colors
                   hover:bg-white/10"
      >
        <Icon name="help" size={15} />
        {/* Label hidden on phones (see mobile.css) — the icon plus the
            aria-label above still name the control for both sighted and
            assistive users, so nothing is lost by dropping the word. */}
        <span className="topbar-help-label">Help</span>
      </button>

      {/* Enter-tenant affordance — platform operators only, and only when not
          already inside a tenant. Routes to the Partner Console where each row's
          "Enter this tenant" button lives. Additive: invisible to normal users. */}
      {canImpersonate && !activeTenantId && (
        <button
          type="button"
          onClick={() => navigate('/settings/tenants')}
          title="Enter a tenant to operate inside their workspace"
          aria-label="Enter a tenant"
          style={{ marginLeft: 8, padding: '6px 12px', background: 'transparent', color: 'var(--t2, var(--text))', border: '1px solid var(--bd, var(--border))', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
        >
          <Icon name="building" size={15} />
          <span>Enter tenant</span>
        </button>
      )}

      {/* Alert badges */}
      <div className="topbar-alerts">
        {incidentsOpen > 0 && (
          <button
            className="ab ab-w"
            onClick={() => navigate('/incidents')}
            type="button"
            title="Open workplace incident reports"
          >
            <span>Open Incidents</span>
            <span className="ab-count">{incidentsOpen}</span>
          </button>
        )}

        {qaPending > 0 && (
          <button
            className="ab ab-pu"
            onClick={() => navigate('/document-qa')}
            type="button"
          >
            <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor">
              <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 11a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm1-3H7V4h2v5z" />
            </svg>
            <span>Doc Questions</span>
            <span className="ab-count">{qaPending}</span>
          </button>
        )}

        {smsPending > 0 && (
          <button
            className="ab ab-g"
            onClick={() => navigate('/sms')}
            type="button"
          >
            <svg viewBox="0 0 16 16" width="13" height="13" fill="currentColor">
              <path d="M14 0H2C.9 0 0 .9 0 2v10c0 1.1.9 2 2 2h4l2 2 2-2h4c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2zM5 9H3V7h2v2zm4 0H7V7h2v2zm4 0h-2V7h2v2z" />
            </svg>
            <span>SMS Pending</span>
            <span className="ab-count">{smsPending}</span>
          </button>
        )}
      </div>

      {/* User menu.
          Was: the whole block navigated to /security, and a bare × next to it
          signed you out on a single click with no confirmation — an easy
          misfire, since × reads as "dismiss", not "end my session". Now the
          block opens a menu and signing out asks first. */}
      <div className="topbar-user" ref={userMenuRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setUserMenuOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          title="Account menu"
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit', textAlign: 'left' }}
        >
          <div className="user-avatar">{initials}</div>
          <div>
            <div className="user-name">{fullName}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span className="mfa-badge">MFA</span>
            </div>
          </div>
          <Icon name="chevron-down" size={14} style={{ opacity: 0.6 }} />
        </button>

        {userMenuOpen && (
          <div role="menu" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, minWidth: 200, background: 'var(--surface-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', boxShadow: 'var(--e-3)', padding: 'var(--sp-1)', zIndex: 1000 }}>
            <div style={{ padding: 'var(--sp-2) var(--sp-3)', borderBottom: '1px solid var(--border)', marginBottom: 'var(--sp-1)' }}>
              <div style={{ fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-strong)' }}>{fullName}</div>
              {user?.primaryEmailAddress?.emailAddress && (
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                  {user.primaryEmailAddress.emailAddress}
                </div>
              )}
            </div>
            <MenuItem icon="shield" label="Security & sign-in" onClick={() => { setUserMenuOpen(false); navigate('/security'); }} />
            <MenuItem icon="bell" label="Notification preferences" onClick={() => { setUserMenuOpen(false); navigate('/settings/notifications'); }} />
            <MenuItem icon="help" label="Help centre" onClick={() => { setUserMenuOpen(false); navigate('/help'); }} />
            <div style={{ height: 1, background: 'var(--border)', margin: 'var(--sp-1) 0' }} />
            <MenuItem icon="door" label="Sign out" destructive onClick={handleSignOut} />
          </div>
        )}
      </div>
      </header>
    </>
  );
}

/** One row in the account menu. */
function MenuItem({ icon, label, onClick, destructive = false }: {
  icon: IconName;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%', background: hover ? (destructive ? 'var(--danger-tint)' : 'var(--brand-hover-bg)') : 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r-sm)', color: destructive ? 'var(--danger-fg)' : 'var(--text)', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-medium)' }}
    >
      <Icon name={icon} size={16} />
      {label}
    </button>
  );
}
